/**
 * Initializes variant selection logic for a product form.
 *
 * @param {Object} config - Configuration object.
 * @param {HTMLElement} config.container - Container element for the product form.
 * @param {Object} config.product - Product JSON object with variants.
 * @param {string} config.singleOptionSelector - Selector for variant option inputs.
 * @param {string} config.originalSelectorId - Selector for hidden master select.
 * @param {boolean} config.enableHistoryState - Whether to update the browser history.
 * @returns {{
  *   getCurrentOptions: () => Array<{ value: string, index: string }>,
  *   getVariantFromOptions: () => Object|null
  * }} - Public API for interacting with variant logic.
  */


function createVariants({ container, product, singleOptionSelector, originalSelectorId, enableHistoryState }) {
  let currentVariant = getVariantFromOptions();

  container.querySelectorAll(singleOptionSelector).forEach(el => el.addEventListener('change', onSelectChange));

 /**
  * Gets the currently selected option values from the form.
  * @returns {Array<{ value: string, index: string }>} Array of selected options.
  */
 function getCurrentOptions() {
 	return Array.from(container.querySelectorAll(singleOptionSelector))
 		.map(el => {
 			const type = el.getAttribute('type');
 			const index = el.dataset.index;
 			if ((type === 'radio' || type === 'checkbox') && !el.checked) return null;
 			return {
 				value: el.value,
 				index
 			};
 		})
 		.filter(Boolean);
 }

 /**
  * Finds the matching variant from selected options.
  * @returns {Object|null} Variant object or null if not found.
  */
  function getVariantFromOptions() {
    const selected = getCurrentOptions();
    return product.variants.find(variant =>
      selected.every(opt => opt.value === variant[opt.index])
    ) || null;
  }

 /**
  * Handles change events on variant option inputs.
  */
  function onSelectChange() {
    const variant = getVariantFromOptions();
    document.dispatchEvent(new CustomEvent('variantChange', { detail: { variant }}));

    if (!variant) return;
    updateMasterSelect(variant);
    updatePrice(variant);
    currentVariant = variant;

    if (enableHistoryState) updateHistoryState(variant);
  }

  function updateMasterSelect(variant) {
    const select = container.querySelector(originalSelectorId);
    if (select) select.value = variant.id;
  }

  function updatePrice(variant) {
    if (
      variant.price === currentVariant?.price &&
      variant.compare_at_price === currentVariant?.compare_at_price
    ) return;
  
    const priceElements = container.querySelectorAll('[data-product-price]');
    const comparePriceElements = container.querySelectorAll('[data-compare-price]');
    const comparePriceTextElements = container.querySelectorAll('[data-compare-text]');
    const savePriceElements = container.querySelectorAll('[data-save-price]');
  
    const compareElements = [
      ...comparePriceElements,
      ...comparePriceTextElements,
      ...savePriceElements
    ];
  
    priceElements.forEach(element => {
      element.innerHTML = POV.Currency.formatMoney(variant.price, POV.Liquid.moneyFormat);
      element.setAttribute('data-product-price', variant.price);
    });
  
    if (variant.compare_at_price > variant.price) {
      comparePriceElements.forEach(element => {
        element.innerHTML = POV.Currency.formatMoney(variant.compare_at_price, POV.Liquid.moneyFormat);
        element.setAttribute('data-compare-price', variant.compare_at_price);
      });
  
      const savings = variant.compare_at_price - variant.price;
      savePriceElements.forEach(element => {
        element.innerHTML = `${POV.Liquid.lang.saveText}${POV.Currency.formatMoney(savings, POV.Liquid.moneyFormat)}`;
      });
  
      compareElements.forEach(element => element?.classList.remove('hide'));
    } else {
      comparePriceElements.forEach(element => {
        element.innerHTML = '';
        element.setAttribute('data-compare-price', '');
      });
  
      compareElements.forEach(element => element?.classList.add('hide'));
    }
  }
  
  function updateHistoryState(variant) {
    const url = `${location.protocol}//${location.host}${location.pathname}?variant=${variant.id}`;
    history.replaceState({ path: url }, '', url);
  }
  
  return {
    getCurrentOptions,
    getVariantFromOptions
  };
}


/**
 * Initializes product logic including variant selection, pricing, color swatches, and preorder.
 *
 * @param {string} containerSelector - CSS selector for the product container.
 * @returns {{
*   container: HTMLElement,
*   productData: Object,
*   selectors: Object,
*   variants: {
*     getCurrentOptions: () => Array<{ value: string, index: string }>,
*     getVariantFromOptions: () => Object|null
*   },
*   selectedVariantId: number|null,
*   getCurrentVariant: () => Object|null,
*   selectVariantById: (variantId: number) => boolean
* }} Public API for the product module.
*/

function createProduct(containerSelector) {
	const container = document.querySelector(containerSelector);
	if (!container) {
    console.error(`Product container not found for selector: ${containerSelector}`);
    return;
  }

	const productJsonEl = container.querySelector('[data-product-json]');
	if (!productJsonEl?.textContent) {
    console.error('Product JSON not found or empty.');
    return;
  }

	const productData = JSON.parse(productJsonEl.textContent);

	const selectors = {
		addToCart: container.querySelector('[data-add-to-cart-main]'),
		addToCartText: container.querySelector('[data-add-to-cart-text]'),
		comparePrice: container.querySelector('[data-compare-price]'),
		comparePriceText: container.querySelector('[data-compare-text]'),
		savePrice: container.querySelector('[data-save-price]'),
		priceWrapper: container.querySelector('[data-price-wrapper]'),
		productForm: container.querySelector('[data-product-form]'),
		productAvailable: container.querySelector('[data-product-available]'),
		singleOptionSelector: '[data-single-option-selector]',
		originalSelectorId: '[data-product-select]'
	};

	const variantsApi = createVariants({
		container,
		product: productData,
		singleOptionSelector: selectors.singleOptionSelector,
		originalSelectorId: selectors.originalSelectorId,
		enableHistoryState: container.dataset.enableHistoryState === 'true'
	});

	let selectedVariant = variantsApi.getVariantFromOptions();

	document.addEventListener('variantChange', e => {
		const variant = e.detail.variant;
		selectedVariant = variant || null;

		// Emit option change event
		POV.emit('variant:change_hotfix', variant);

		if (variant) {
			// Emit variant change event
			POV.emit('variant:change', variant);

			selectors.priceWrapper?.classList.remove('hide');
			selectors.productForm?.classList.remove('variant-unavailable');

			if (variant.featured_media && window.POV?.Gallery) {
				POV.Gallery.goToId(variant.featured_media.id);
			}

			if (variant.available) {
				selectors.addToCart?.removeAttribute('disabled');
				selectors.addToCartText && (selectors.addToCartText.innerHTML = POV.Liquid.lang.addToCart);
				selectors.productAvailable?.setAttribute('data-product-available', 'true');
			} else {
				selectors.addToCart?.setAttribute('disabled', 'true');
				selectors.addToCartText && (selectors.addToCartText.innerHTML = POV.Liquid.lang.soldOut);
				selectors.productAvailable?.setAttribute('data-product-available', 'false');
			}
		} else {
			selectors.addToCart?.setAttribute('disabled', 'true');
			selectors.addToCartText && (selectors.addToCartText.innerHTML = POV.Liquid.lang.unavailable);
			selectors.productAvailable?.setAttribute('data-product-available', 'unavailable');
			selectors.productForm?.classList.add('variant-unavailable');
		}
	});

  document.addEventListener('variantChange', e => {
    const variant = e.detail.variant;
    if (variant) updateColor(variant);
    if (variant) renderPreorder(variant);
  })


  function updateColor(variant) {
		const colorInput = document.querySelector('input[name="colour-swatch"]:checked');
		const colorEl = document.querySelector('[data-js-color]');

		if (!variant && colorInput && colorEl) {
			colorEl.textContent = colorInput.dataset.value;
			return;
		}

		const colorIndex = productData.options.indexOf('Color');
		if (colorIndex !== -1 && colorEl) {
			colorEl.textContent = variant.options[colorIndex];
		}
  }

	return {
		container,
		productData,
		selectors,
		variants: variantsApi,

		/** @type {number|null} ID of the currently selected variant */
		get selectedVariantId() {
			return selectedVariant?.id || null;
		},

		/**
		 * Gets the currently selected variant object.
		 * @returns {Object|null}
		 */
		getCurrentVariant() {
			return selectedVariant;
		},

		/**
		 * Programmatically selects a variant by its ID.
		 * @param {number} variantId - Variant ID to select.
		 * @returns {boolean} Whether the variant was found and selection triggered.
		 */
		selectVariantById(variantId) {
			const variant = productData.variants.find(v => v.id === variantId);
			if (!variant) return false;

			variant.options.forEach((value, index) => {
				const input = container.querySelector(`[data-single-option-selector][data-index="option${index + 1}"]`);
				if (input) {
					input.value = value;
					input.dispatchEvent(new Event('change', {
						bubbles: true
					}));
				}
			});

			return true;
		}
	};
}


function renderPreorder(variant) {
  const configEl = document.querySelector('[data-preorder]');
  if (!configEl || !variant) return;

  const config = JSON.parse(configEl.textContent);
  const variantConfig = config[variant.id];
  const wrapper = document.querySelector('[data-section-type="product"]');
  const msg = document.querySelector('[data-preorder-msg]');
  const date = document.querySelector('[data-preorder-date]');
  const atc = document.querySelector('[data-add-to-cart-text]');

  if (variantConfig && variantConfig.msg && variantConfig.inventory_policy === 'continue' && variantConfig.inventory_quantity <= 0) {
    wrapper?.classList.add('pre-order');
    date.textContent = variantConfig.msg;
    msg?.removeAttribute('hidden');
    if (atc) atc.textContent = POV.Liquid.lang.preOrder;
  } else {
    msg?.setAttribute('hidden', true);
    wrapper?.classList.remove('pre-order');
    if (atc) atc.textContent = POV.Liquid.lang.addToCart;
  }
}

// Swatch Change
function setupSwatchChange() {
  document.querySelectorAll('.product-swatches input').forEach(swatch => {
    swatch.addEventListener('change', () => {
      const value = swatch.dataset.value;
      const option = swatch.dataset.option;
      const selector = `[data-single-option-selector][data-index="option${option}"]`;
      const el = document.querySelector(selector);
      if (el) {
        el.value = value;
        el.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  });
}

// Buy Bar
function setupBuyBar() {
  if (!POV?.support?.IntersectionObserver) return;

  const target = document.querySelector('[data-buy-bar-target-trigger]');
  const bar = document.querySelector('[data-buy-bar]');
  const buyBtn = document.querySelector('[data-buy-bar] [data-buy-btn]');
  const atcBtn = document.querySelector('[data-add-to-cart-main]');

  if (!target || !bar || !buyBtn || !atcBtn) return;

  document.addEventListener('variantChange', e => {
    bar.setAttribute('data-buy-bar-available', Boolean(e.detail.variant?.available));
  });

  buyBtn.addEventListener('click', () => atcBtn.click());

  const observer = new IntersectionObserver(entries => {
    const isVisible = entries[0].boundingClientRect.y < entries[0].rootBounds.top;
    bar.toggleAttribute('data-buy-bar-hidden', !isVisible);
    document.body.classList.toggle('buy-bar-visible', isVisible);
  }, {
    rootMargin: '-25% 0px -25% 0px',
    threshold: 1
  });

  observer.observe(target);
}

// Review Badge
function setupReviewBadge() {
  const badge = document.querySelector('[data-review-badge]');
  if (!badge) return;

  document.addEventListener('stamped:reviews:loaded', () => {
    const minCount = Number(badge.dataset.reviewBadgeMinCount);
    const count = Number(document.querySelector('[data-rating="5"]')?.closest('[data-count]')?.dataset.count || 0);
    const countDisplay = badge.querySelector('[data-review-badge-count]');

    if (count >= minCount && countDisplay) {
      countDisplay.textContent = Math.floor(count / 10) * 10;
      badge.removeAttribute('hidden');
    }
  });
}

// Init
document.addEventListener('DOMContentLoaded', () => {
  POV.Product = createProduct('[data-section-type="product"]');
  setupBuyBar();
  setupReviewBadge();
  setupSwatchChange();

  const preloadEl = document.querySelector('[data-preorder-variant]');
  if (preloadEl) {
    renderPreorder(JSON.parse(preloadEl.textContent));
  }
});
